/**
 * Shared TAP (Traits and Personalization) delivery client.
 *
 * Provides fetch utilities for both user-level and site-level TAP traits,
 * along with common parsers for trait values.
 */

export type Trait = {
	name: string;
	value: string | boolean | number;
};

type PersonalizationResponse = {
	attributes: Trait[];
};

const getUserTraitsUrl = (userId: string) =>
	`/gateway/api/tap-delivery/api/v3/personalization/user/${userId}`;

const getSiteTraitsUrl = (siteId: string) =>
	`/gateway/api/tap-delivery/api/v3/personalization/site/${siteId}`;

const fetchTraits = async (url: string, signal?: AbortSignal): Promise<Trait[]> => {
	const response = await fetch(url, {
		method: 'GET',
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
			'x-atlassian-mau-ignore': 'true',
		},
		signal,
	});

	if (!response.ok) {
		throw new Error(`TAP request failed with status ${response.status}`);
	}

	const data: PersonalizationResponse = await response.json();
	return data.attributes ?? [];
};

/**
 * Fetches user-level TAP traits.
 *
 * @param userId - The Atlassian account ID of the user
 * @param signal - Optional AbortSignal so callers can apply a timeout / cancel the request
 * @returns Array of traits, or throws on failure
 */
export const fetchUserTraits = async (userId: string, signal?: AbortSignal): Promise<Trait[]> => {
	return fetchTraits(getUserTraitsUrl(userId), signal);
};

/**
 * Fetches site-level TAP traits.
 *
 * @param siteId - The cloud ID of the site
 * @param signal - Optional AbortSignal so callers can apply a timeout / cancel the request
 * @returns Array of traits, or throws on failure
 */
export const fetchSiteTraits = async (siteId: string, signal?: AbortSignal): Promise<Trait[]> => {
	return fetchTraits(getSiteTraitsUrl(siteId), signal);
};

/**
 * Finds a trait by name and returns its value, or undefined if not found.
 */
export const findTraitValue = (
	traits: Trait[],
	traitName: string,
): string | boolean | number | undefined => {
	const trait = traits.find((t) => t.name === traitName);
	return trait?.value;
};

/**
 * Parses a comma-separated "product:value" trait string into a Record.
 *
 * Example trait value: "drive:42,figma:18,slack:73"
 * Returns: { drive: 42, figma: 18, slack: 73 }
 */
export const parseProductValueTrait = (
	traits: Trait[],
	traitName: string,
): Record<string, number> => {
	const trait = traits.find((t) => t.name === traitName);

	if (!trait || typeof trait.value !== 'string') {
		return {};
	}

	const result: Record<string, number> = {};
	const pairs = trait.value.split(',').map((s) => s.trim());

	for (const pair of pairs) {
		const [product, valueStr] = pair.split(':').map((s) => s.trim());
		if (product && valueStr) {
			const value = Number(valueStr);
			if (Number.isFinite(value)) {
				result[product] = value;
			}
		}
	}

	return result;
};

/**
 * Maps TAP-trait product keys to the FE source keys returned by the
 * assistance-service AgentKnowledgeConfiguration API.
 * Source of truth: convoai-platform `KnowledgeSourceTypeValues`.
 */
const TAP_KEY_TO_FE_SOURCE: Record<string, string> = {
	adobexd: 'adobe_xd',
	'adobe-sign': 'adobe_sign',
	aha: 'aha',
	airtable: 'airtable',
	amplitude: 'amplitude',
	asana: 'asana',
	'atlassian-web-crawler': 'atlassian-web-crawler',
	'aws-s3': 'aws',
	'azure-devops': 'azure_devops',
	box: 'box',
	canva: 'canva',
	clickup: 'clickup',
	databricks: 'databricks',
	docusign: 'docusign',
	dovetail: 'dovetail',
	dropbox: 'dropbox',
	figma: 'figma',
	freshservice: 'freshservice',
	gcp: 'gcp',
	github: 'github',
	gitbook: 'gitbook',
	gitlab: 'gitlab',
	gmail: 'gmail',
	'google-calendar': 'google_calendar',
	drive: 'google_drive',
	hubspot: 'hubspot',
	launchdarkly: 'launchdarkly',
	loom: 'loom',
	lucid: 'lucid',
	miro: 'miro',
	monday: 'monday',
	mural: 'mural',
	notion: 'notion',
	onedrive: 'microsoft_onedrive',
	'outlook-calendar': 'microsoft_outlook_calendar',
	'outlook-mail': 'microsoft_outlook_mail',
	pagerduty: 'pagerduty',
	pipedrive: 'pipedrive',
	'power-bi': 'microsoft_power_bi',
	salesforce: 'salesforce',
	sentry: 'sentry',
	servicenow: 'servicenow',
	sharepoint: 'microsoft_sharepoint',
	slack: 'slack',
	smartsheet: 'smartsheet',
	stripe: 'stripe',
	teams: 'teams',
	todoist: 'todoist',
	webex: 'cisco_webex',
	zendesk: 'zendesk',
	zeplin: 'zeplin',
};

export const normalizeTapKeys = (parsed: Record<string, number>): Record<string, number> => {
	const normalized: Record<string, number> = {};
	for (const [tapKey, value] of Object.entries(parsed)) {
		const normalizedKey = TAP_KEY_TO_FE_SOURCE[tapKey] ?? tapKey;
		normalized[normalizedKey] = Math.max(
			normalized[normalizedKey] ?? Number.NEGATIVE_INFINITY,
			value,
		);
	}
	return normalized;
};

/**
 * Per-connector % of MAU users authed to that 3P, sorted desc by pct.
 * Trait value format: "product:pct,product:pct,..." (e.g. "slack:72,figma:45,drive:30")
 */
export const CONNECTOR_AUTH_RATE_TRAIT = 'rovo_connector_authed_users_percentage_by_app';
